import { StrictMode, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import posthog from './lib/posthog';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { Toaster } from 'sonner';
import { ConversationSimulation } from './screens/ConversationSimulation';
import { AgentBuilder } from './screens/AgentBuilder';
import { EvaluatorBuilder } from './screens/EvaluatorBuilder';
import { PersonaBuilder } from './screens/PersonaBuilder';
import { Evaluations } from './screens/Evaluations';
import { ApiSettings } from './screens/ApiSettings';
import { Connections } from './screens/Connections';
import { Auth } from './components/Auth';
import { AuthCallback } from './components/AuthCallback';
import { Tutorial } from './components/Tutorial';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useAuthStore } from './stores/authStore';
import { useSettingsStore } from './stores/settingsStore';
import { supabase } from './lib/supabase';
import { TooltipProvider } from './components/ui/tooltip';
import { useSimulationStore } from './stores/simulationStore';
import { AppProviders } from './components/AppProviders';

function App() {
  const { user, loading, setUser, fetchWorkspaceProfiles } = useAuthStore();
  const { loadSettings } = useSettingsStore();
  const { resetSimulation } = useSimulationStore();

  const cleanUpSession = () => {
    resetSimulation();
    setUser(null);
    localStorage.removeItem('currentWorkspace');
  };

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        loadSettings(user?.currentWorkspace?.workspace_id);
        setUser({
          ...session?.user,
          currentWorkspace: null,
          workspace_profiles: null,
        });
        fetchWorkspaceProfiles(session?.user);
      } else {
        setUser(null);
        const psDistId = new URLSearchParams(window.location.search).get(
          'ps_dist',
        );
        if (psDistId) {
          posthog.identify(psDistId);
        }
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        posthog.opt_in_capturing({ captureEventName: false });
        posthog.reset();
        cleanUpSession();
      }
      if (event === 'PASSWORD_RECOVERY' && session?.user) {
        setUser({
          ...session.user,
          currentWorkspace: null,
          workspace_profiles: null,
        });
        window.location.replace('/auth/reset');
        return;
      }
      if (event === 'SIGNED_IN') {
        const currentUser = useAuthStore.getState().user;
        if (!currentUser && session?.user) {
          setUser({
            ...session?.user,
            currentWorkspace: null,
            workspace_profiles: null,
          });
          loadSettings(user?.currentWorkspace?.workspace_id);
          fetchWorkspaceProfiles(session.user);
        }
      }
      if (event === 'TOKEN_REFRESHED' && session?.user) {
        const currentUser = useAuthStore.getState().user;
        setUser({
          ...currentUser,
          ...session.user,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [setUser, loadSettings]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-prompt-safecomgallery">
        <div className="text-prompt-safecomvulcan">Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            user ? <Navigate to="/agent-builder" /> : <Navigate to="/auth" />
          }
        />
        <Route
          path="/auth"
          element={user ? <Navigate to="/agent-builder" /> : <Auth />}
        />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/reset" element={<Auth />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppProviders>
                <Tutorial />
                <Outlet />
              </AppProviders>
            </ProtectedRoute>
          }
        >
          <Route path="evaluations" element={<Evaluations />} />
          <Route path="settings" element={<ApiSettings />} />
          <Route path="connections" element={<Connections />} />
          <Route path="api-settings" element={<Navigate to="/settings" />} />
          <Route
            path="conversation-simulation/:simulationId?"
            element={<ConversationSimulation />}
          />
          <Route path="agent-builder/:agentId?" element={<AgentBuilder />} />
          <Route
            path="persona-builder/:personaId?"
            element={<PersonaBuilder />}
          />
          <Route
            path="evaluator-builder/:evaluatorId?"
            element={<EvaluatorBuilder />}
          />
        </Route>
      </Routes>
    </Router>
  );
}

Sentry.init({
  dsn: 'https://1abda2aedb66d760cbc04f00663d12ec@o4509417928196096.ingest.de.sentry.io/4509428644118608',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
});

createRoot(document.getElementById('app') as HTMLElement).render(
  <StrictMode>
    <TooltipProvider>
      <App />
      <Toaster position="bottom-center" richColors />
    </TooltipProvider>
  </StrictMode>,
);
